import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router'
import { HugeiconsIcon } from '@hugeicons/react'
import {
    File02Icon,
    LibraryIcon,
    Login03Icon,
    Logout03Icon,
    User03Icon,
    UserAccountIcon,
    UserIcon,
    UserQuestion01Icon,
    UserSettings01Icon,
} from '@hugeicons-pro/core-bulk-rounded/index'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import {
    Avatar,
    Box,
    Button,
    IconButton,
    ListItem,
    ListItemAvatar,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
} from '@mui/material'

import { useGetContactQuery, useGetCustomerQuery } from '@/app/services/customer'
import { useGetStoreSettingsQuery } from '@/app/services/organisation'
import { selectContactId, unsetContactId } from '@/app/slices/contactsSlice'
import { selectCustomerId, unsetCustomerId } from '@/app/slices/customerSlice'
import { selectGuestUser, selectIsGuestLoggedIn, unsetGuestUser } from '@/app/slices/guestUserSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import TbxToolbar from '@/common/components/TbxToolbar/TbxToolbar'
import TbxToolbarActions from '@/common/components/TbxToolbar/TbxToolbarActions'
import TbxToolbarDivider from '@/common/components/TbxToolbar/TbxToolbarDivider'
import TbxToolbarTitle from '@/common/components/TbxToolbar/TbxToolbarTitle'
import { useToolBoxTreatments } from '@/common/hooks/useToolBoxTreatments'
import { Paths } from '@/common/utils'

import TbxDialog from '../TbxDialog/TbxDialog'

const classes = {
    appBarTitle: {
        fontWeight: 500,
        fontSize: '18px',
        lineHeight: '21px',
        textTransform: 'uppercase',
        color: 'text.primary',
    },
}

const AppToolbar = ({ children = null }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { showAccountSettings, showOrderHistory, showPartLibrary } = useToolBoxTreatments()

    const { isAuthenticated, login, logout, user } = useKindeAuth()

    const organisationId = useSelector(selectOrganisationId)
    const currentCustomerId = useSelector(selectCustomerId)
    const currentContactId = useSelector(selectContactId)
    const isGuestLoggedIn = useSelector(selectIsGuestLoggedIn)
    const currentGuest = useSelector(selectGuestUser)

    const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)
    const [loginClicked, setLoginClicked] = useState(false)

    const { data: storeSettings } = useGetStoreSettingsQuery({
        organisationId,
    })

    const { data: currentCustomer } = useGetCustomerQuery(
        {
            organisationId,
            customerId: currentCustomerId,
        },
        { skip: !currentCustomerId }
    )

    const { data: currentContact } = useGetContactQuery(
        {
            organisationId,
            customerId: currentCustomerId,
            contactId: currentContactId,
        },
        { skip: !currentContactId || !currentCustomerId }
    )

    const open = Boolean(anchorEl)

    const handleLogoutDialogOpen = () => setIsLogoutDialogOpen(true)

    const handleIsLogoutDialogClose = () => setIsLogoutDialogOpen(false)

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const handleCloseMenu = () => {
        setAnchorEl(null)
    }

    const handleLogOut = () => {
        setAnchorEl(null)
        handleIsLogoutDialogClose()
        navigate(Paths.LOGOUT_PATHNAME)
    }

    const handleLogIn = () => {
        setLoginClicked(true)
        if (isGuestLoggedIn) {
            dispatch(unsetCustomerId())
            dispatch(unsetContactId())
            dispatch(unsetGuestUser())
        }
        login({ org_code: storeSettings?.kindeOrganisationId, app_state: { redirectTo: window.location.href } })
    }

    return (
        <TbxToolbar>
            <TbxToolbarTitle sx={classes.appBarTitle}>
                {storeSettings?.webStoreDisplayName ?? storeSettings?.organisationName}
            </TbxToolbarTitle>

            <TbxToolbarActions>
                {children}

                <TbxToolbarDivider />

                <Box className="profileIcon">
                    {isAuthenticated || isGuestLoggedIn ? (
                        <>
                            <IconButton
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                aria-label="account of current user"
                                color="primary"
                                size="small"
                                onClick={handleMenu}
                            >
                                {isAuthenticated ? (
                                    <Avatar
                                        alt={currentContact?.name}
                                        sx={{ bgcolor: 'secondary.main' }}
                                    >
                                        {user.given_name.charAt(0).toUpperCase() +
                                            user.family_name.charAt(0).toUpperCase()}
                                    </Avatar>
                                ) : (
                                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                        <HugeiconsIcon
                                            color="white"
                                            icon={User03Icon}
                                        />
                                    </Avatar>
                                )}
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                id="menu-appbar"
                                open={open}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                keepMounted
                                onClose={handleCloseMenu}
                            >
                                <ListItem
                                    key="profile"
                                    divider
                                >
                                    <ListItemAvatar>
                                        {isAuthenticated ? (
                                            <Avatar
                                                alt={currentContact?.name}
                                                sx={{ bgcolor: 'secondary.main' }}
                                            >
                                                {user.given_name.charAt(0).toUpperCase() +
                                                    user.family_name.charAt(0).toUpperCase()}
                                            </Avatar>
                                        ) : (
                                            <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                                <HugeiconsIcon
                                                    color="white"
                                                    icon={User03Icon}
                                                />
                                            </Avatar>
                                        )}
                                    </ListItemAvatar>
                                    {isAuthenticated && currentContactId && currentCustomerId ? (
                                        <ListItemText
                                            primary={currentContact.name}
                                            secondary={currentCustomer?.companyName || ''}
                                        />
                                    ) : (
                                        <ListItemText
                                            primary={currentGuest?.name}
                                            secondary={t('Guest user')}
                                        />
                                    )}
                                </ListItem>
                                <TbxToolbarDivider />

                                {isAuthenticated && currentCustomerId
                                    ? [
                                          showOrderHistory && (
                                              <MenuItem
                                                  component={Link}
                                                  key="orders"
                                                  to="/my-orders"
                                              >
                                                  <ListItemIcon sx={{ color: 'text.primary' }}>
                                                      <HugeiconsIcon icon={File02Icon} />
                                                  </ListItemIcon>
                                                  <ListItemText primary={t('My orders')} />
                                              </MenuItem>
                                          ),
                                          showPartLibrary && (
                                              <MenuItem
                                                  component={Link}
                                                  key="parts"
                                                  to="/my-parts"
                                              >
                                                  <ListItemIcon sx={{ color: 'text.primary' }}>
                                                      <HugeiconsIcon icon={LibraryIcon} />
                                                  </ListItemIcon>
                                                  <ListItemText primary={t('My parts')} />
                                              </MenuItem>
                                          ),
                                          showAccountSettings && (
                                              <MenuItem
                                                  component={Link}
                                                  key="account"
                                                  to="/account"
                                                  divider
                                              >
                                                  <ListItemIcon sx={{ color: 'text.primary' }}>
                                                      <HugeiconsIcon icon={UserSettings01Icon} />
                                                  </ListItemIcon>
                                                  <ListItemText primary={t('My account')} />
                                              </MenuItem>
                                          ),
                                      ]
                                    : null}
                                <MenuItem
                                    key="logout"
                                    onClick={handleLogoutDialogOpen}
                                >
                                    <ListItemIcon sx={{ color: 'text.primary' }}>
                                        <HugeiconsIcon icon={Logout03Icon} />
                                    </ListItemIcon>
                                    <ListItemText primary={t('Log out')} />
                                </MenuItem>
                            </Menu>
                        </>
                    ) : (
                        <Button
                            aria-label="login"
                            color="primary"
                            loading={loginClicked}
                            loadingPosition="start"
                            size="small"
                            startIcon={<HugeiconsIcon icon={Login03Icon} />}
                            onClick={handleLogIn}
                        >
                            {t('Log in')}
                        </Button>
                    )}
                </Box>
            </TbxToolbarActions>

            <TbxDialog
                closeButtonText={t('cancel')}
                confirmButtonText={t('Yes, Log out')}
                content={t('Are you sure you want to log out? You will be navigated away from the current order.')}
                handleClose={handleIsLogoutDialogClose}
                handleConfirmClose={handleLogOut}
                isOpen={isLogoutDialogOpen}
                title={t('Log out')}
                uniqueKey={currentContactId || 'NO_CONTACT'}
            />
        </TbxToolbar>
    )
}

export default AppToolbar
